import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Layout } from './components/Layout';
import { LoadingPage } from './components/common';

/**
 * Routes.
 *
 * Everything below the bill document is lazily loaded — the print stylesheet
 * and the PDF-shaped bill layout are large and only a fraction of visitors
 * open them.
 */

const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const PropertyDashboardPage = lazy(() => import('./pages/PropertyDashboardPage'));
const BillsPage = lazy(() => import('./pages/BillsPage'));
const BillViewPage = lazy(() => import('./pages/BillViewPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ReceiptPage = lazy(() => import('./pages/ReceiptPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PropertyCheckoutPage = lazy(() => import('./pages/PropertyCheckoutPage'));
const GatewayPage = lazy(() => import('./pages/GatewayPage'));
const PropertyGatewayPage = lazy(() => import('./pages/PropertyGatewayPage'));
const PaymentResultPage = lazy(() => import('./pages/PaymentResultPage'));
const PropertyPaymentResultPage = lazy(() => import('./pages/PropertyPaymentResultPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const SchemesPage = lazy(() => import('./pages/SchemesPage'));
const ServiceWizardPage = lazy(() => import('./pages/ServiceWizardPage'));
const MyApplicationsPage = lazy(() => import('./pages/MyApplicationsPage'));
const ApplicationPage = lazy(() => import('./pages/ApplicationPage'));
const TrackApplicationPage = lazy(() => import('./pages/TrackApplicationPage'));
const NdcVerifyPage = lazy(() => import('./pages/NdcVerifyPage'));
const NdcCertificatePage = lazy(() => import('./pages/NdcCertificatePage'));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const FaqsPage = lazy(() => import('./pages/FaqsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const MyQueriesPage = lazy(() => import('./pages/MyQueriesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export function App() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />

          <Route path="/property/:propertyId" element={<PropertyDashboardPage />} />
          <Route path="/property/:propertyId/bills" element={<BillsPage />} />
          <Route path="/property/:propertyId/bills/:billId" element={<BillViewPage />} />
          <Route path="/property/:propertyId/history" element={<HistoryPage />} />

          <Route path="/pay/checkout/property/:propertyId" element={<PropertyCheckoutPage />} />
          <Route path="/pay/checkout/:billId" element={<CheckoutPage />} />
          <Route path="/pay/gateway/property" element={<PropertyGatewayPage />} />
          <Route path="/pay/gateway/:txnNo" element={<GatewayPage />} />
          <Route path="/pay/result/property" element={<PropertyPaymentResultPage />} />
          <Route path="/pay/result/:txnNo" element={<PaymentResultPage />} />
          <Route path="/receipt/:receiptId" element={<ReceiptPage />} />
          <Route path="/receipts/:receiptId" element={<ReceiptPage />} />

          <Route path="/property/:propertyId/services" element={<ServicesPage />} />
          <Route path="/property/:propertyId/schemes" element={<SchemesPage />} />
          <Route path="/property/:propertyId/services/:code/apply" element={<ServiceWizardPage />} />
          <Route path="/property/:propertyId/applications" element={<MyApplicationsPage />} />
          <Route path="/property/:propertyId/applications/:appId" element={<ApplicationPage />} />
          <Route path="/property/:propertyId/applications/:appId/receipt" element={<ReceiptPage />} />
          <Route path="/property/:propertyId/applications/:appId/certificate" element={<NdcCertificatePage />} />
          <Route path="/property/:propertyId/queries" element={<MyQueriesPage />} />
          <Route path="/property/:propertyId/notifications" element={<NotificationsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:code/apply" element={<ServiceWizardPage />} />
          <Route path="/applications/track" element={<TrackApplicationPage />} />
          <Route path="/applications/:appId" element={<ApplicationPage />} />
          <Route path="/applications/:appId/receipt" element={<ReceiptPage />} />
          <Route path="/applications/:appId/certificate" element={<NdcCertificatePage />} />
          <Route path="/verify/ndc" element={<NdcVerifyPage />} />

          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/faqs" element={<FaqsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />

          {/* Legacy PHP deep links people may have bookmarked. */}
          <Route path="/citizen/search.php" element={<Navigate to="/search" replace />} />
          <Route path="/index.php" element={<Navigate to="/" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
